import { useQuery } from '@tanstack/react-query';

import { QUERY_PRESETS } from '@/lib/query';

import {
  APPLICATION_INSTANCES,
  IN_HOUSE_PACKAGES,
  SELF_REPORT_CONFIRMATION_QUESTION_COLUMNS,
  SELF_REPORT_CONFIRMATION_ROWS,
  SELF_REPORT_INSTANCES,
  SELF_REPORT_PACKAGE_NAME,
  SELF_REPORT_PATTERN_CONFIGS,
  SELF_REPORT_PATTERNS,
  SELF_REPORT_QUESTION_COLUMNS,
} from '../-mock/fixtures';

import type {
  InHousePackage,
  InHousePackagePattern,
  SelfReportConfirmationQuestionColumn,
  SelfReportConfirmationRow,
  SelfReportInstanceRow,
  SelfReportPatternConfig,
  SelfReportQuestionColumn,
} from './type';

/**
 * テナント専用パッケージのローカルモック取得層。
 *
 * NOTE: バックエンド並行開発中のため、ここで完結するモックを返す。
 * 実 API 確定後は本ファイル内の各関数の中身のみを差し替える
 *（呼び出し側のルート/コンポーネントは変更不要）。
 */

/** テナント専用パッケージ一覧（サイドバー用） */
export function listInHousePackages(): InHousePackage[] {
  return structuredClone(IN_HOUSE_PACKAGES);
}

/** セルフレポートのパターン一覧 */
export function listSelfReportPatterns(
  _packageId: string,
): InHousePackagePattern[] {
  return structuredClone(SELF_REPORT_PATTERNS);
}

/** セルフレポートのインスタンス一覧（グルーピング表示用） */
export function listSelfReportInstances(
  _patternId: string,
): SelfReportInstanceRow[] {
  return structuredClone(SELF_REPORT_INSTANCES);
}

/** セルフレポート一覧の Form 設問列定義 */
export function getSelfReportQuestionColumns(): SelfReportQuestionColumn[] {
  return structuredClone(SELF_REPORT_QUESTION_COLUMNS);
}

/** セルフレポート確認画面の対象者別回答一覧 */
export function listSelfReportConfirmationRows(
  _patternId: string,
): SelfReportConfirmationRow[] {
  return structuredClone(SELF_REPORT_CONFIRMATION_ROWS);
}

/** セルフレポート確認画面の Form 設問列定義 */
export function getSelfReportConfirmationQuestionColumns(): SelfReportConfirmationQuestionColumn[] {
  return structuredClone(SELF_REPORT_CONFIRMATION_QUESTION_COLUMNS);
}

/** 申請パッケージのインスタンス一覧（モック） */
export function listApplicationInstances(_packageId: string) {
  return structuredClone(APPLICATION_INSTANCES);
}

/** PatternTypeTabs の進行中タスク数取得 hook（モック） */
export function useInHousePackageTaskCountInProgress(arg: {
  operationPackageId: string;
  patternId: string;
}) {
  return useQuery({
    queryKey: [
      'in-house-package-task-count',
      arg.operationPackageId,
      arg.patternId,
    ],
    queryFn: () =>
      Promise.resolve({
        operationPackagePatternId: arg.patternId,
        count: SELF_REPORT_INSTANCES.length,
      }),
    ...QUERY_PRESETS.list(),
  });
}

/**
 * セルフレポートのパターン設定インメモリストア（モック）。
 *
 * 編集モードの初期値（fixtures）で seed し、新規作成分は本ストアへ登録する。
 * 画面遷移後も `config/$patternId` 経由で同じ ID を解決できるよう module スコープに保持する。
 */
const selfReportPatternConfigStore = new Map<string, SelfReportPatternConfig>(
  SELF_REPORT_PATTERN_CONFIGS.map((config) => [
    config.operationPackagePatternId,
    structuredClone(config),
  ]),
);

/** 指定 ID がセルフレポートのパターンか（config/$patternId の分岐判定用） */
export function isSelfReportPatternId(patternId: string): boolean {
  return selfReportPatternConfigStore.has(patternId);
}

/** セルフレポートのパターン設定を取得（設問エディタの初期値） */
export function getSelfReportPatternConfig(
  patternId: string,
): SelfReportPatternConfig | undefined {
  const config = selfReportPatternConfigStore.get(patternId);
  return config ? structuredClone(config) : undefined;
}

/**
 * セルフレポートのパターンを新規作成する（モック）。
 * 1 問だけの白紙テンプレートをストアへ登録し、CreatePatternDialog 互換の戻り値を返す。
 */
export function createSelfReportPattern(values: {
  name: string;
  description?: string;
}): {
  operationPackagePatternId: string;
  name: string;
  description?: string;
  anchorDateLabel: string;
}[] {
  const operationPackagePatternId = `sr-pat-${crypto.randomUUID()}`;
  const config: SelfReportPatternConfig = {
    operationPackagePatternId,
    packageName: SELF_REPORT_PACKAGE_NAME,
    name: values.name,
    description: values.description ?? '',
    dueDate: null,
    status: 'draft',
    createdBy: null,
    updatedBy: null,
    updatedAt: null,
    questions: [
      {
        id: `q-${crypto.randomUUID()}`,
        type: 'radio',
        title: '',
        description: '',
        required: true,
        options: [{ id: `opt-${crypto.randomUUID()}`, label: '' }],
      },
    ],
  };
  selfReportPatternConfigStore.set(operationPackagePatternId, config);
  return [
    {
      operationPackagePatternId,
      name: values.name,
      description: values.description,
      anchorDateLabel: '',
    },
  ];
}

/**
 * セルフレポートのパターン設定を保存する（モック）。
 * 下書き保存 / 公開の双方で呼ぶ。publish=true で status を published に更新する。
 */
export function saveSelfReportPatternConfig(
  config: SelfReportPatternConfig,
  options?: { publish?: boolean },
): void {
  selfReportPatternConfigStore.set(config.operationPackagePatternId, {
    ...structuredClone(config),
    status: options?.publish ? 'published' : config.status,
  });
}
